/**
 * 2段階認証の「必須にする」設定（テナントごと）の読み方と判定。**純粋な関数だけ**（画面と API の両方から使う）。
 *
 * 保存先は既存の security_settings.login_policy（jsonb・テナントごとに1行）。新しい表・列は作らない。
 *   - requireTwoFactor     … true で必須（既存の型にあったキー。今まで判定には使われていなかった）
 *   - twoFactorRequiredAt  … 「必須にする」ボタンで必須にした日時（ISO）。**これが無い true は必須として扱わない**
 *                            （ボタンを経ずに入った古い true で、ある日突然全員が誘導されないように）
 *   - twoFactorNoticeStart … 案内の帯を出し始める日（日本時間の YYYY-MM-DD）。空＝帯を出さない（既定）
 *   - twoFactorDeadline    … 登録の締め切り日（日本時間の YYYY-MM-DD）。帯に出す
 *   - twoFactorRequiredIfInactiveDays … しばらく使っていない社員だけ必須にする日数（正の整数）。空＝働かない（既定・今と同じ）
 *                            （AUTH-01・2026-10-29。会社一律の必須とは別の条件。判定は isInactiveForTwoFactor）
 *
 * 決定（2026-10-07）：期限を設けて登録してもらう → 期限後に管理画面のボタンでテナントの社員に必須 →
 * 未設定の人を一覧で見る。未登録の人は締め出さず、ログインは通して登録画面にだけ誘導する。
 * 管理者が2人以上登録済みでないと必須にできない（ロックアウトを防ぐ）。
 *
 * 根拠：OWASP MFA Cheat Sheet「Require some form of MFA for all users.」
 *   https://cheatsheetseries.owasp.org/cheatsheets/Multifactor_Authentication_Cheat_Sheet.html
 */
import { MFA_SETTINGS_HREF } from '@/lib/auth/mfa-settings-location'

/** 必須にするのに要る「登録済みの管理者」の人数。 */
export const MIN_ENROLLED_ADMINS_TO_REQUIRE = 2

/** 登録へ誘導中の人を API が断ったときの応答の code（lib/api/auth.ts）。画面はこの code で断られた理由を見分ける。 */
export const TWO_FACTOR_REQUIRED_CODE = 'TWO_FACTOR_REQUIRED'

/**
 * 登録のお願いの1文（理由は書かない）。覆い・断られたときの知らせ（右上・サイドバー）・登録の窓の赤字で同じ文を使う
 * （AUTH-01 補足5・補足7・2026-10-31）。
 */
export const TWO_FACTOR_ENROLL_REQUEST = '2段階認証（認証アプリ）の登録をお願いいたします。'

export interface TwoFactorPolicy {
  /** このテナントで必須か（ボタンで必須にしたときだけ true）。 */
  required: boolean
  /** 案内の帯を出し始める日（YYYY-MM-DD）。未設定（既定）は null＝帯を出さない。 */
  noticeStart: string | null
  /** 登録の締め切り日（YYYY-MM-DD）。未設定は null。 */
  deadline: string | null
  /** 必須にした日時（ISO）。 */
  requiredAt: string | null
  /** この日数より長く使っていない社員だけ必須にする（AUTH-01）。未設定（既定）は null＝働かない。 */
  inactiveDays: number | null
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** YYYY-MM-DD の実在する日付か。 */
export function isValidDeadline(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return false
  const d = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value
}

/** login_policy（jsonb）から2段階認証の設定を読む。形が違う値は「必須でない」に倒す。 */
export function readTwoFactorPolicy(loginPolicy: unknown): TwoFactorPolicy {
  const p =
    loginPolicy && typeof loginPolicy === 'object' ? (loginPolicy as Record<string, unknown>) : {}
  const requiredAt =
    typeof p.twoFactorRequiredAt === 'string' && p.twoFactorRequiredAt !== ''
      ? p.twoFactorRequiredAt
      : null
  return {
    required: p.requireTwoFactor === true && requiredAt !== null,
    noticeStart: isValidDeadline(p.twoFactorNoticeStart) ? p.twoFactorNoticeStart : null,
    deadline: isValidDeadline(p.twoFactorDeadline) ? p.twoFactorDeadline : null,
    requiredAt,
    inactiveDays: isPositiveInteger(p.twoFactorRequiredIfInactiveDays)
      ? p.twoFactorRequiredIfInactiveDays
      : null,
  }
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

const DAY_MS = 86_400_000

/**
 * しばらく使っていない社員か（AUTH-01 ③）。対象＝最後に使った日（users.last_active_at）が inactiveDays 日より前、
 * または一度も無い人。ただし作られて inactiveDays 日以内の人（入ったばかり）は除く。
 * 線は本番の数え（`last_at < now() - interval '30 days'`・`created_at >= now() - interval '30 days'`）と同じ＝
 * ちょうど N 日前は対象でない。
 *
 * 判定は「この要求より前の」最後に使った日で行う。呼び元は対象の間 last_active_at を書き換えない
 * （書くと次の要求で使っている人に変わり、1回で必須が外れる・lib/api/auth.ts）。
 * 最後に使った日・作られた日が取れていない（undefined）ときは対象にしない（取れないことを理由に止めない）。
 */
export function isInactiveForTwoFactor(input: {
  inactiveDays: number | null
  lastActiveAt: Date | null | undefined
  createdAt: Date | null | undefined
  now: Date
}): boolean {
  if (input.inactiveDays === null) return false
  if (input.lastActiveAt === undefined || !input.createdAt) return false
  const line = input.now.getTime() - input.inactiveDays * DAY_MS
  if (input.createdAt.getTime() >= line) return false
  return input.lastActiveAt === null || input.lastActiveAt.getTime() < line
}

/** 2段階認証の設定のキー。汎用のセキュリティ設定の保存では、ここは変えさせない。 */
export const TWO_FACTOR_POLICY_KEYS = [
  'requireTwoFactor',
  'twoFactorRequiredAt',
  'twoFactorRequiredBy',
  'twoFactorDeadline',
  'twoFactorNoticeStart',
  'twoFactorRequiredIfInactiveDays',
] as const

/**
 * 汎用の保存（PATCH /api/security/settings）で来た loginPolicy から2段階認証のキーを外し、
 * 今の値をそのまま残す。必須の切り替えは専用の口（管理者2人の条件つき）だけで行う。
 */
export function keepTwoFactorKeys(
  incoming: Record<string, unknown>,
  current: unknown
): Record<string, unknown> {
  const cur = current && typeof current === 'object' ? (current as Record<string, unknown>) : {}
  const out: Record<string, unknown> = { ...incoming }
  for (const key of TWO_FACTOR_POLICY_KEYS) {
    if (key in cur) out[key] = cur[key]
    else delete out[key]
  }
  return out
}

/** 「必須にする」を押せるか。押せないときは画面に出す理由を返す。 */
export function canRequireTwoFactor(
  enrolledAdminCount: number
): { ok: true } | { ok: false; reason: string } {
  if (enrolledAdminCount >= MIN_ENROLLED_ADMINS_TO_REQUIRE) return { ok: true }
  return {
    ok: false,
    reason: `管理者が${MIN_ENROLLED_ADMINS_TO_REQUIRE}人以上2段階認証を登録するまで必須にできません（今 ${enrolledAdminCount} 人）。全員が締め出されないための条件です。`,
  }
}

/** 必須のテナントで、この人を登録画面へ誘導するか。登録済み・取れていない（undefined）は誘導しない。 */
export function mustEnrollTwoFactor(input: {
  required: boolean
  twoFactorEnabled: boolean | undefined
}): boolean {
  return input.required && input.twoFactorEnabled === false
}

/**
 * 今の画面が誘導先そのものか。誘導先に ?a=b が付いていれば（例：プロフィールのセキュリティの節
 * `/guide?account=security`）、パスが同じで、その値も全部そろっているときだけ誘導先とみなす
 * （パスだけで見ると /guide が丸ごと対象外になるため）。
 */
function isEnrollLocation(pathname: string, search: string): boolean {
  const target = new URL(MFA_SETTINGS_HREF, 'http://local')
  if (pathname !== target.pathname) return false
  const current = new URLSearchParams(search)
  for (const [k, v] of target.searchParams) {
    if (current.get(k) !== v) return false
  }
  return true
}

/**
 * 誘導の対象外の画面（誘導先＝プロフィールのセキュリティの節・ログイン時の6桁の画面）。誘導先を外さないと行き来が止まらない。
 * search は今の URL の ?…（無ければ空）。
 */
export function isTwoFactorExemptPath(pathname: string | null | undefined, search = ''): boolean {
  if (!pathname) return false
  if (pathname === '/two-factor') return true
  return isEnrollLocation(pathname, search)
}

/** 締め切り日の表示（例：10月24日）。 */
export function formatDeadlineJa(deadline: string): string {
  const [, m, d] = deadline.split('-')
  return `${Number(m)}月${Number(d)}日`
}
